// src/pages/Gallery.js - Updated to match SuperAdminDashboard API structure
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaExpand, FaTimes } from 'react-icons/fa';
import { adminAPI } from '../services/api';

const Gallery = () => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentCategory, setCurrentCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const categories = [
    { key: 'all', label: 'All' },
    { key: 'competitions', label: 'Competitions' },
    { key: 'awards', label: 'Awards' },
    { key: 'moments', label: 'Moments' },
    { key: 'events', label: 'Events' },
    { key: 'officials', label: 'Officials' }
  ];

  // Fetch images from backend - SAME AS SUPERADMINDASHBOARD
  useEffect(() => {
    const fetchGallery = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('Fetching gallery...');
        
        const response = await adminAPI.getGallery();
        console.log('Gallery response:', response);
        
        if (response && response.gallery) {
          setGalleryItems(response.gallery);
          console.log('Gallery items loaded:', response.gallery.length);
        } else {
          setGalleryItems([]);
          console.log('No gallery items found');
        }
      } catch (error) {
        console.error('Failed to load gallery:', error);
        setError(error.message || 'Failed to load gallery images');
        setGalleryItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, []);

  // Category filter
  const filteredItems =
    currentCategory === 'all'
      ? galleryItems
      : galleryItems.filter((item) => item.category === currentCategory);

  return (
    <div className="min-h-screen bg-gray-50 pt-20 lg:pt-24">
      {/* Header */}
      <section className="px-4 sm:px-6 lg:px-8 py-16">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-block px-4 py-2 bg-green-100 rounded-full border border-green-200 mb-6">
              <span className="text-green-700 text-sm font-medium">Event Gallery</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              <span className="text-gray-900">Moments That</span>
              <br />
              <span className="text-green-600 drop-shadow-sm">Inspire & Transform</span>
            </h1>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Witness the transformative journey of Africa's next generation of leaders
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {categories.map((category) => (
              <button
                key={category.key}
                onClick={() => setCurrentCategory(category.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  currentCategory === category.key
                    ? 'bg-green-500 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-green-50 hover:text-green-600 shadow-sm border border-gray-200'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-7xl">
          {loading ? (
            <div className="text-center text-gray-500 py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mb-4"></div>
              <p>Loading gallery...</p>
            </div>
          ) : error ? (
            <div className="text-center text-red-500 py-20">
              <p className="mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center text-gray-500 py-20">
              <p className="text-lg">No images found in this category.</p>
              {currentCategory !== 'all' && (
                <button
                  onClick={() => setCurrentCategory('all')}
                  className="mt-4 px-6 py-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  View All Images
                </button>
              )}
            </div>
          ) : (
            <motion.div
              layout
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
            >
              <AnimatePresence>
                {filteredItems.map((item, index) => (
                  <motion.div
                    key={item._id || index}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="group cursor-pointer"
                    onClick={() => setSelectedImage(item)}
                  >
                    <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                      <div className="aspect-[4/3] overflow-hidden">
                        <img
                          src={item.imageUrl}
                          alt={item.title || 'Gallery image'}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>

                      {/* Title Overlay (if available) */}
                      {item.title && (
                        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <p className="text-white text-sm font-medium">{item.title}</p>
                        </div>
                      )}

                      {/* Expand Icon */}
                      <div className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-110">
                        <FaExpand className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </section>

      {/* Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative max-w-5xl max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-black/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/40 transition-colors z-10"
              >
                <FaTimes className="w-4 h-4" />
              </button>

              <div className="w-full">
                <img
                  src={selectedImage.imageUrl}
                  alt={selectedImage.title || 'Gallery image'}
                  className="w-full max-h-[85vh] object-contain"
                />
                {selectedImage.title && (
                  <div className="p-4 bg-gray-50 border-t">
                    <h3 className="text-lg font-semibold text-gray-800">{selectedImage.title}</h3>
                    <p className="text-sm text-gray-500 capitalize mt-1">{selectedImage.category}</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
